/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Server-safe schema set used by `renderToMarkdown`. Mirrors the editor's
 * `CommonExtensions` + `CustomComponentsExtensions` (web/src/tiptap/...) in
 * SCHEMA terms only: stock @tiptap/* extensions (already pure ProseMirror) plus
 * the React-free custom-node stubs in ./custom-extensions. No node-views, no
 * commands, no `@/` web imports — so importing this never drags the browser
 * bundle into the server's module graph (issue #520).
 *
 * Only schema-affecting extensions are included. Editor-only plugins that add no
 * node/mark/attribute to a stored document (SlashCommand, Placeholder,
 * CharacterCount, Dropcursor, Typography, Markdown, Selection, AI, drag-handle…)
 * are intentionally omitted — they don't affect Node.fromJSON or serialization.
 */
import StarterKit from '@tiptap/starter-kit';
import { Document } from '@tiptap/extension-document';
import Emoji, { gitHubEmojis } from '@tiptap/extension-emoji';
import UniqueID from '@tiptap/extension-unique-id';
import { TaskItem } from '@tiptap/extension-task-item';
import { TaskList } from '@tiptap/extension-task-list';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import TiptapImage from '@tiptap/extension-image';
import TiptapLink from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Youtube from '@tiptap/extension-youtube';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';

import { CommentMarkStub } from "../../markdown/comment-mark-stub";
import { CustomSchemaExtensions } from "./custom-extensions";
import { CommitmentKeyExtension } from "../../commitment-key-extension";

// Editor uses `content: '(block|columns)*'` so top-level `columns` nodes parse.
const DocumentExtension = Document.extend({ content: '(block|columns)*' });

export const ServerRenderExtensions = [
  DocumentExtension,
  StarterKit.configure({ document: false }),
  Emoji.configure({ emojis: gitHubEmojis, forceFallbackImages: true }),
  // Adds the `id` global attribute to these types so `:dg{id="…"}` directives
  // survive Node.fromJSON. The generateID/filterTransaction plugin bits are
  // editor-runtime only and don't affect static serialization, so they're dropped.
  UniqueID.configure({
    types: [
      'heading',
      'paragraph',
      'blockquote',
      'codeBlock',
      'table',
      'Accordion',
      'AccordionGroup',
      'Admonition',
      'InternalNote',
      'Card',
      'CardGroup',
      'Steps',
      'Step',
      'Screenshot',
      'Tab',
      'Tabs',
    ],
    attributeName: 'id',
  }),
  CommitmentKeyExtension,
  TaskItem.configure({ nested: true }),
  TaskList,
  Table.configure({ resizable: true }),
  TableRow,
  TableHeader,
  TableCell,
  TiptapImage,
  Mention.configure({ HTMLAttributes: { class: 'mention' } }),
  TextStyle,
  Color,
  Highlight.configure({ multicolor: true }),
  Youtube,
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  Underline,
  Subscript,
  Superscript,
  TiptapLink.configure({ openOnClick: false }),
  CommentMarkStub,
  ...CustomSchemaExtensions,
] as any[];
